import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";

import { format, addDays, addMonths, isSameMonth } from "date-fns";

import axios from '@app/lib/axios';

import CommonHorizontalBarChart from "@app/components/CommonHorizontalBarChart";
import CommonVerticalBarChart from "@app/components/CommonVerticalBarChart";

const appConnectChartOption = {
    xAxis: { field: "statDate" },
    yAxis: {
        name: "건",
        series: [
            { name: "안드로이드", field: "connectAndroid", stack: "base", option: { itemStyle: { color: "#60d7c5" } } },
            { name: "웹", field: "connectWeb", stack: "base", option: { itemStyle: { color: "#b9ed4a" } } },
        ],
    },
    showLegend: true,
    legend: {
        x: "right",
        y: "top"
    },
    showSum: true,
    sort: false,
    grid: {
        top: "15%",
        left: "5%",
        right: "5%"
    },
};
const appNewChartOption = {
    xAxis: { field: "statDate" },
    yAxis: {
        name: "건",
        series: [{ name: "앱 설치 수", field: "newUser", stack: "base", type: "line" }],
    },
    stacks: [{ stack: "base", showPercent: true, showSum: false, showSumTooltip: false }],
    showLegend: true,
    legend: {
        x: "right",
        y: "top"
    },
    sort: false,
    grid: {
        top: "15%",
        left: "5%",
        right: "5%"
    },
    showPercent: false,
};
const appAgeChartOption = {
    xAxis: { field: "statDate" },
    yAxis: {
        name: "건",
        series: [
            { name: "만2세", field: "age0Play", stack: "base" },
            { name: "만3세", field: "age1Play", stack: "base" },
            { name: "만4세", field: "age2Play", stack: "base" },
            { name: "만5세", field: "age3Play", stack: "base" },
        ],
    },
    showLegend: true,
    legend: {
        x: "right",
        y: "top"
    },
    showSum: true,
    sort: false,
    grid: {
        top: "15%",
        left: "5%",
        right: "5%"
    },
};
const contentChartOption = {
    xAxis: { field: "statDate" },
    yAxis: {
        name: "건",
        series: [
            { name: "교실영어", field: "playContentCountClass", stack: "base", option: { itemStyle: { color: "#3574dc" } } },
            { name: "스페셜", field: "playContentCountSpecial", stack: "base", option: { itemStyle: { color: "#b293ee" } } },
        ],
    },
    showLegend: true,
    legend: {
        x: "right",
        y: "top"
    },
    showSum: true,
    sort: false,
    grid: {
        top: "15%",
        left: "5%",
        right: "5%"
    },
};
const contentPlayChartOption = {
    xAxis: { field: "statDate" },
    yAxis: {
        name: "건",
        series: [
            { name: "교실영어", field: "playCountClass", stack: "base", option: { itemStyle: { color: "#3574dc" } } },
            { name: "스페셜", field: "playCountSpecial", stack: "base", option: { itemStyle: { color: "#b293ee" } } },
        ],
    },
    showLegend: true,
    legend: {
        x: "right",
        y: "top"
    },
    showSum: true,
    sort: false,
    grid: {
        top: "15%",
        left: "5%",
        right: "5%"
    },
};
const App = forwardRef(({ statPeriod }, ref) => {
    const [statApp, setStatApp] = useState(false);
    const [statAppAge, setStatAppAge] = useState(false);
    const [statContent, setStatContent] = useState(false);

    useImperativeHandle(ref, () => ({
        getExcelData(callback) {
            const excelData = {
                fileName: '플라잉클래스_앱통계',
                sheets: [
                    {name: '접속', title: '접속 수 통계', columnTitle:['', '앱', '웹'], data: statApp ? statApp.map((s) => [s.statDate, s.connectAndroid, s.connectWeb]) : []},
                    {name: '설치', title: '앱 설치 수 통계', columnTitle:['', '설치 수'], data: statApp ? statApp.map((s) => [s.statDate, s.newUser]) : []},
                    {name: '연령', title: '연령별 사용량 통계', columnTitle:['', '만2세', '만3세', '만4세', '만5세'], data: statAppAge ? statAppAge.map((s) => [s.statDate, s.age0Play, s.age1Play, s.age2Play, s.age3Play]) : []},
                    {name: '콘텐츠', title: '콘텐츠 실행 통계', columnTitle:['', '교실(순)', '스페셜(순)', '교실(누적)', '스페셜(누적)'], data: statContent ? statContent.map((s) => [s.statDate, s.playContentCountClass, s.playContentCountSpecial, s.playCountClass, s.playCountSpecial]) : []},
                ]
            };
            callback(null, excelData);
        },
    }));

    const getStat = useCallback(() => {
        axios({
            method: "POST",
            url: `/api/flyeng_stat/new/class/app`,
            data: {
                startDate: statPeriod.startDate,
                endDate: statPeriod.endDate,
                limit: 5,
                periodType: statPeriod.type,
            },
        })
            .then((result) => {
                if (result.data.code === 200) {
                    const stat = result.data.data;
                    let statApp = [];
                    let statAppAge = [];
                    let statContent = [];

                    let start = new Date(statPeriod.startDate);
                    let day = new Date(statPeriod.startDate);
                    for(let i = 0; i < 99; i++) {
                        const statDate = format(day,  statPeriod.type === 'year' ? "yyyy-MM" : "yyyy-MM-dd");
                        const statDateStr = format(day,  statPeriod.type === 'year' ? "yyyy-MM" : "MM-dd");
                        const sApp = stat.app.find((s) => s.statDate === statDate);
                        const sAge = stat.appAge.find((s) => s.statDate === statDate);
                        const sContent = stat.content.find((s) => s.statDate === statDate);
                        statApp.push({...sApp, statDate: statDateStr});
                        statAppAge.push({...sAge, statDate: statDateStr});
                        statContent.push({...sContent, statDate: statDateStr});
                        if(statPeriod.type === 'week') {
                            day = addDays(day, 1);
                            if(i >= 6) break;
                        } else if(statPeriod.type === 'month') {
                            day = addDays(day, 1);
                            if(!isSameMonth(day, start)) break;
                        } else {    // year
                            day = addMonths(day, 1);
                            if(i > 11) break;
                        }
                    }
                    setStatApp(statApp);
                    setStatAppAge(statAppAge);
                    setStatContent(statContent);
                } else {
                    alert(result.data && result.data.msg ? result.data.msg : "통계 정보를 가져오는데 실패하였습니다.\n지속 발생시 관리자에게 문의해주세요.");
                }
            })
            .catch((error) => {
                console.log(error);
                console.error("알 수 없는 오류가 발생하였습니다. \n지속 발생시 관리자에게 문의해주세요.");
            });
    }, [statPeriod]);
    useEffect(() => {
        getStat();
        return () => {};
    }, [getStat]);

    return (
        <div className="w-100">
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">접속 수</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonHorizontalBarChart chartData={statApp} chartOption={appConnectChartOption} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">설치 수</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonHorizontalBarChart chartData={statApp} chartOption={appNewChartOption} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">연령별 사용량</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonHorizontalBarChart chartData={statAppAge} chartOption={appAgeChartOption} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">콘텐츠 순 실행 수</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonHorizontalBarChart chartData={statContent} chartOption={contentChartOption} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">콘텐츠 누적 실행 수</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonHorizontalBarChart chartData={statContent} chartOption={contentPlayChartOption} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default App;
